import { useEffect, useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { MGMT, fmtD, dayjs, today } from "@/lib/constants";
import { useHotel } from "@/context/HotelContext";
import { PageHeader } from "@/components/Common";
import { CrudPage } from "@/components/CrudPage";
import { Badge } from "@/components/Badges";

const COURSES = ["Sicurezza lavoro D.Lgs. 81/08", "Primo Soccorso", "Antincendio rischio elevato", "BLSD", "Hospitality Security Academy", "Privacy e GDPR"];

const STAFF_FIELDS = [
  { key: "hotel_id", label: "Struttura", type: "hotel", required: true },
  { key: "name", label: "Nome e cognome", required: true },
  { key: "role", label: "Ruolo", type: "select", options: ["Shift Leader", "Operatore Fiduciario", "Jolly", "Back-up"], required: true },
  { key: "phone", label: "Telefono" },
  { key: "languages", label: "Lingue" },
  { key: "hired_on", label: "Data assunzione", type: "date" },
  { key: "active", label: "Stato", type: "select", options: ["attivo", "non attivo"] },
];
const STAFF_COLS = [
  { key: "name", label: "Nome" },
  { key: "role", label: "Ruolo", render: (r) => <Badge tone={r.role === "Jolly" || r.role === "Back-up" ? "info" : r.role === "Shift Leader" ? "warn" : "neutral"}>{r.role}</Badge> },
  { key: "phone", label: "Telefono" },
  { key: "languages", label: "Lingue" },
  { key: "hired_on", label: "Assunto", render: (r) => fmtD(r.hired_on) },
  { key: "active", label: "Stato" },
];

const expTone = (d) => (!d ? "neutral" : d < today() ? "bad" : d <= dayjs().add(30, "day").format("YYYY-MM-DD") ? "warn" : "ok");

const TRAIN_FIELDS = [
  { key: "hotel_id", label: "Struttura", type: "hotel", required: true },
  { key: "staff_id", label: "Operatore", type: "staff", required: true },
  { key: "course", label: "Corso", type: "select", options: COURSES, required: true, full: true },
  { key: "completed_on", label: "Data conseguimento", type: "date", required: true },
  { key: "expires_on", label: "Scadenza", type: "date", required: true },
];

const Matrix = () => {
  const { hotelId, staff, hotelName } = useHotel();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/trainings", { params: hotelId !== "all" ? { hotel_id: hotelId } : {} }).then((r) => setRows(r.data)); }, [hotelId]);
  const people = staff.filter((s) => hotelId === "all" || s.hotel_id === hotelId);
  const map = useMemo(() => {
    const m = {};
    rows.forEach((t) => { const k = `${t.staff_id}|${t.course}`; if (!m[k] || m[k].expires_on < t.expires_on) m[k] = t; });
    return m;
  }, [rows]);
  const valid = rows.filter((t) => t.expires_on >= today()).length;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 text-xs text-slate-500 items-center">
        <span className="font-heading text-2xl font-bold text-amber-600" data-testid="training-valid-pct">{rows.length ? Math.round((valid / rows.length) * 1000) / 10 : 100}%</span>
        <span>attestati in validità</span>
        <Badge tone="ok">valido</Badge><Badge tone="warn">in scadenza 30 gg</Badge><Badge tone="bad">scaduto</Badge>
      </div>
      <div className="panel overflow-x-auto" data-testid="training-matrix">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left p-3 eyebrow">Operatore</th>
              {COURSES.map((c) => <th key={c} className="p-3 eyebrow text-center font-normal">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {people.map((p) => (
              <tr key={p.id} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="p-3"><div className="text-slate-900">{p.name}</div><div className="text-xs text-slate-500">{p.role}{hotelId === "all" && ` · ${hotelName(p.hotel_id)}`}</div></td>
                {COURSES.map((c) => {
                  const t = map[`${p.id}|${c}`];
                  return <td key={c} className="p-3 text-center">{t ? <Badge tone={expTone(t.expires_on)}>{fmtD(t.expires_on)}</Badge> : <Badge tone="bad">mancante</Badge>}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function Personale() {
  const { reloadStaff, staffName } = useHotel();
  const trainCols = [
    { key: "staff_id", label: "Operatore", render: (r) => staffName(r.staff_id) },
    { key: "course", label: "Corso" },
    { key: "completed_on", label: "Conseguito", render: (r) => fmtD(r.completed_on) },
    { key: "expires_on", label: "Scadenza", render: (r) => <Badge tone={expTone(r.expires_on)}>{fmtD(r.expires_on)}</Badge> },
  ];
  return (
    <>
      <PageHeader eyebrow="Team dedicato" title="Personale & Formazione" subtitle="Anagrafica del team titolare, Jolly e back-up con matrice formativa e scadenze degli attestati." />
      <Tabs defaultValue="matrice">
        <TabsList>
          <TabsTrigger value="matrice" data-testid="tab-training-matrix">Matrice formativa</TabsTrigger>
          <TabsTrigger value="corsi" data-testid="tab-trainings">Attestati</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff">Personale</TabsTrigger>
        </TabsList>
        <TabsContent value="matrice" className="mt-4"><Matrix /></TabsContent>
        <TabsContent value="corsi" className="mt-4">
          <CrudPage resource="trainings" title="Attestato" testid="trainings" fields={TRAIN_FIELDS} columns={trainCols} writeRoles={MGMT} newLabel="Nuovo attestato" />
        </TabsContent>
        <TabsContent value="staff" className="mt-4">
          <CrudPage resource="staff" title="Operatore" testid="staff" fields={STAFF_FIELDS} columns={STAFF_COLS} writeRoles={MGMT} newLabel="Nuovo operatore"
            defaults={{ role: "Operatore Fiduciario", active: "attivo" }} onChanged={reloadStaff} />
        </TabsContent>
      </Tabs>
    </>
  );
}
